// Centrifugo realtime — live messages, reactions, typing, agent status,
// plus per-user notify + workspace presence streams.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { WS_URL, api, Message, PublicUser, ReactionAgg } from './api';
import { PREVIEW } from './mock';

let cf: Centrifuge | null = null;

// Private channels: Centrifugo requires a per-channel token, which the API mints
// only after an access check. The client fetches one on subscribe + refresh.
function subOpts(channel: string) {
  return { getToken: () => api.subscribeToken(channel).then((r) => r.token) };
}

function client(): Centrifuge {
  if (!cf) {
    cf = new Centrifuge(WS_URL, {
      getToken: async () => (await api.realtimeToken()).token,
    });
    cf.connect();
    // Resilience: if the tab was backgrounded/slept and the socket dropped, nudge a
    // reconnect the moment it's foregrounded again (centrifuge re-subscribes its
    // channels on reconnect) — so live messages resume without a manual refresh.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && cf && cf.state !== 'connected') {
          try { cf.connect(); } catch { /* already (re)connecting */ }
        }
      });
    }
  }
  return cf;
}

// Shared, ref-counted subscriptions so multiple consumers (e.g. the chat view AND
// an in-call chat panel) can listen to the same channel without a duplicate-sub error.
type SharedEntry = { sub: any; count: number; handlers: Set<(ctx: any) => void> };
const shared: Record<string, SharedEntry> = {};

export function sharedSub(channel: string, onPub: (ctx: any) => void): () => void {
  if (PREVIEW) return () => {};
  let entry = shared[channel];
  if (!entry) {
    const cfx = client();
    const sub = cfx.newSubscription(channel, subOpts(channel));
    entry = { sub, count: 0, handlers: new Set() };
    shared[channel] = entry;
    sub.on('publication', (ctx: any) => entry!.handlers.forEach((h) => h(ctx)));
    sub.subscribe();
  }
  entry.count += 1;
  entry.handlers.add(onPub);
  return () => {
    entry!.handlers.delete(onPub);
    entry!.count -= 1;
    if (entry!.count <= 0) {
      try {
        entry!.sub.unsubscribe();
        client().removeSubscription(entry!.sub);
      } catch { /* ignore */ }
      delete shared[channel];
    }
  };
}

export type TypingSignal = { actor: string; name: string };
export type AgentStatus = { agentId: string; name: string; emoji: string; text?: string };

function applyReaction(
  reactions: ReactionAgg[] | undefined,
  emoji: string,
  actor: string,
  add: boolean,
): ReactionAgg[] {
  const list = (reactions || []).map((r) => ({ ...r, actors: [...r.actors] }));
  const entry = list.find((r) => r.emoji === emoji);
  if (add) {
    if (!entry) return [...list, { emoji, count: 1, actors: [actor] }];
    if (!entry.actors.includes(actor)) {
      entry.actors.push(actor);
      entry.count += 1;
    }
    return list;
  }
  if (entry) {
    entry.actors = entry.actors.filter((a) => a !== actor);
    entry.count = entry.actors.length;
  }
  return list.filter((r) => r.count > 0);
}

export type PollLive = Record<string, { counts: Record<string, number>; total_voters: number; closed: boolean }>;

export type PromptLive = Record<string, { status: string; selected: string[] }>;

export type ChannelState = {
  messages: Message[];
  typing: TypingSignal[];
  agentStatuses: AgentStatus[];
  pollLive: PollLive;
  promptLive: PromptLive;
  reads: Record<string, string>;   // userId → their last_read_at (ISO); powers read receipts
  hasMore: boolean;                // false once history is exhausted
  loadOlder: () => Promise<number>; // prepend the previous page; resolves to how many arrived
  addLocalEcho: (msg: Message) => void;
  confirmLocalEcho: (tempId: string, real: Message) => void;
  failLocalEcho: (tempId: string) => void;
};

/** History + live message/reaction/typing/agent-status events for a channel. */
export function useChannelMessages(channelId: string | null): ChannelState {
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState<TypingSignal[]>([]);
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>([]);
  const [pollLive, setPollLive] = useState<PollLive>({});
  const [promptLive, setPromptLive] = useState<PromptLive>({});
  const [reads, setReads] = useState<Record<string, string>>({});
  // per-actor timers so we can auto-expire transient typing signals
  const timers = useRef<Record<string, number>>({});
  // History pagination: the server pages newest-first, PAGE at a time.
  const PAGE = 50;
  const [hasMore, setHasMore] = useState(false);
  const messagesRef = useRef<Message[]>([]);
  messagesRef.current = messages;
  const loadingOlder = useRef(false);
  const loadOlder = useCallback(async (): Promise<number> => {
    const oldest = messagesRef.current[0]?.created_at;
    if (!channelId || !oldest || loadingOlder.current) return 0;
    loadingOlder.current = true;
    try {
      const older = await api.messages(channelId, { before: oldest, limit: PAGE });
      if (older.length < PAGE) setHasMore(false);
      if (older.length) {
        setMessages((cur) => {
          const seen = new Set(cur.map((m) => m.id));
          return [...older.filter((m) => !seen.has(m.id)), ...cur];
        });
      }
      return older.length;
    } catch {
      return 0;
    } finally {
      loadingOlder.current = false;
    }
  }, [channelId]);

  useEffect(() => {
    setTyping([]);
    setAgentStatuses([]);
    setPollLive({});
    setPromptLive({});
    setReads({});
    setHasMore(false);
    if (!channelId) {
      setMessages([]);
      return undefined;
    }
    let alive = true;
    api
      .messages(channelId)
      .then((m) => {
        if (alive) { setMessages(m); setHasMore(m.length >= PAGE); }
      })
      .catch(() => {});
    // Initial read cursors of the other members (for "Seen by …").
    api
      .channelReads(channelId)
      .then((rs) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        rs.forEach((r) => { if (r.last_read_at) map[r.user_id] = r.last_read_at; });
        setReads(map);
      })
      .catch(() => {});

    const off = sharedSub(`channel:${channelId}`, (ctx: any) => {
      const d = ctx?.data;
      if (!d?.type) return;

      if (d.type === 'read' && d.user_id && d.last_read_at) {
        setReads((cur) => ((cur[d.user_id] || '') >= d.last_read_at ? cur : { ...cur, [d.user_id]: d.last_read_at }));
      } else if (d.type === 'message.new' && d.message) {
        setMessages((cur) =>
          cur.some((x) => x.id === d.message.id) ? cur : [...cur, d.message],
        );
        // a new message clears that sender's typing signal
        setTyping((cur) => cur.filter((t) => t.actor !== `${d.message.sender_type}:${d.message.sender_id}`));
      } else if (d.type === 'message.updated' && d.message) {
        // keep locally-known reactions — the edit payload doesn't carry them;
        // merge in edit + pin state.
        setMessages((cur) =>
          cur.map((m) =>
            m.id === d.message.id
              ? {
                  ...m,
                  body: d.message.body,
                  edited_at: d.message.edited_at,
                  pinned: d.message.pinned,
                  pinned_at: d.message.pinned_at,
                  pinned_by: d.message.pinned_by,
                  is_announcement: d.message.is_announcement,
                  flagged_dlp: d.message.flagged_dlp,
                }
              : m,
          ),
        );
      } else if (d.type === 'message.update' && d.message_id) {
        // Transcript-ready (voice/video): swap in the fresh attachments carrying
        // transcript + transcript_status; leave everything else on the message intact.
        setMessages((cur) =>
          cur.map((m) =>
            m.id === d.message_id ? { ...m, attachments: d.attachments ?? m.attachments } : m,
          ),
        );
      } else if (d.type === 'message.deleted' && d.message_id) {
        setMessages((cur) => cur.filter((m) => m.id !== d.message_id));
      } else if (d.type === 'channel.deleted') {
        // The whole channel is gone — let the app close it and refresh the sidebar.
        window.dispatchEvent(new CustomEvent('fx:channel-deleted', { detail: { channelId } }));
      } else if (d.type === 'members.changed') {
        // Someone added/removed a person or agent — viewers refetch the member list.
        window.dispatchEvent(new CustomEvent('fx:members-changed', { detail: { channelId } }));
      } else if (d.type === 'reaction.add' || d.type === 'reaction.remove') {
        setMessages((cur) =>
          cur.map((m) =>
            m.id === d.message_id
              ? { ...m, reactions: applyReaction(m.reactions, d.emoji, d.actor, d.type === 'reaction.add') }
              : m,
          ),
        );
      } else if (d.type === 'typing' && d.actor) {
        setTyping((cur) => {
          const rest = cur.filter((t) => t.actor !== d.actor);
          return [...rest, { actor: d.actor, name: d.name || 'Someone' }];
        });
        window.clearTimeout(timers.current[d.actor]);
        timers.current[d.actor] = window.setTimeout(() => {
          setTyping((cur) => cur.filter((t) => t.actor !== d.actor));
        }, 4000);
      } else if (d.type === 'agent.status') {
        setAgentStatuses((cur) => {
          const rest = cur.filter((s) => s.agentId !== d.agent_id);
          return d.emoji ? [...rest, { agentId: d.agent_id, name: d.name, emoji: d.emoji, text: d.text || '' }] : rest;
        });
      } else if (d.type === 'poll.update' && d.poll_id) {
        setPollLive((cur) => ({
          ...cur,
          [d.poll_id]: { counts: d.counts || {}, total_voters: d.total_voters || 0, closed: !!d.closed },
        }));
      } else if (d.type === 'prompt.update' && d.prompt_id) {
        setPromptLive((cur) => ({
          ...cur,
          [d.prompt_id]: { status: d.status || 'answered', selected: d.selected || [] },
        }));
      } else if (d.type === 'thread.reply' && d.root_id) {
        // Bump the root message's reply summary in the main timeline (the reply itself
        // is not added to the flat list — it lives in the thread panel).
        setMessages((cur) =>
          cur.map((m) =>
            m.id === d.root_id
              ? { ...m, reply_count: (m.reply_count || 0) + 1, last_reply_at: d.message?.created_at || m.last_reply_at }
              : m,
          ),
        );
      }
    });

    return () => {
      alive = false;
      Object.values(timers.current).forEach((t) => window.clearTimeout(t));
      timers.current = {};
      off();
    };
  }, [channelId]);

  // Optimistic local echo: the sender's message appears instantly with a temp id;
  // confirm() swaps in the server row (or drops the temp if the realtime echo
  // beat it); fail() removes it so the composer can restore the draft.
  const addLocalEcho = (msg: Message) => setMessages((cur) => [...cur, msg]);
  const confirmLocalEcho = (tempId: string, real: Message) =>
    setMessages((cur) => {
      const withoutTemp = cur.filter((m) => m.id !== tempId);
      return withoutTemp.some((m) => m.id === real.id) ? withoutTemp : [...withoutTemp, real];
    });
  const failLocalEcho = (tempId: string) =>
    setMessages((cur) => cur.filter((m) => m.id !== tempId));

  return { messages, typing, agentStatuses, pollLive, promptLive, reads, hasMore, loadOlder, addLocalEcho, confirmLocalEcho, failLocalEcho };
}

export type NotifyEvent = {
  channel_id: string;
  channel_kind: string;
  workspace_id: string;
  message: Message;
  // Server-side targeted relevance (thread participant / mention) — the client
  // treats these as breakthrough rather than channel chatter.
  targeted?: boolean;
  thread?: boolean;
};
export type RingEvent = {
  type?: 'call.ring' | 'call.cancel';
  channel_id: string;
  channel_name?: string | null;
  channel_kind: string;
  from_id: string;
  from_name: string;
  video: boolean;
};

export type WorkspaceEvent = {
  type: 'join.request' | 'join.decided';
  workspace_id: string;
  workspace_name?: string;
  from_name?: string;
  status?: 'approved' | 'denied';
};
export type ReminderEvent = {
  type: 'event.reminder';
  id: string;
  title: string;
  starts_at: string | null;
  minutes: number;
  is_call: boolean;
  location: string | null;
  lead: string;
};

export type SuggestionEvent = {
  type: 'suggestion.new';
  suggestion: {
    id: string;
    channel_id: string;
    kind: 'response' | 'action_item' | 'doc_link' | 'knowledge_gap';
    payload: Record<string, any>;
    confidence: number;
    trigger_message_id: string | null;
  };
};

/** Personal stream: message notifies + incoming call rings + workspace join events. */
export function useUserStream(
  userId: string | null,
  onNotify: (e: NotifyEvent) => void,
  onRing?: (e: RingEvent) => void,
  onWorkspaceEvent?: (e: WorkspaceEvent) => void,
  onReminder?: (e: ReminderEvent) => void,
  onSuggestion?: (e: SuggestionEvent) => void,
): void {
  const cbN = useRef(onNotify);
  cbN.current = onNotify;
  const cbR = useRef(onRing);
  cbR.current = onRing;
  const cbW = useRef(onWorkspaceEvent);
  cbW.current = onWorkspaceEvent;
  const cbRem = useRef(onReminder);
  cbRem.current = onReminder;
  const cbS = useRef(onSuggestion);
  cbS.current = onSuggestion;
  useEffect(() => {
    if (!userId || PREVIEW) return undefined;
    const cfx = client();
    const uch = `user:${userId}`;
    const sub = cfx.newSubscription(uch, subOpts(uch));
    sub.on('publication', (ctx: any) => {
      const d = ctx?.data;
      if (d?.type === 'notify' && d.message) cbN.current(d as NotifyEvent);
      else if (d?.type === 'call.ring' || d?.type === 'call.cancel') cbR.current?.(d as RingEvent);
      else if (d?.type === 'join.request' || d?.type === 'join.decided') cbW.current?.(d as WorkspaceEvent);
      else if (d?.type === 'event.reminder') cbRem.current?.(d as ReminderEvent);
      else if (d?.type === 'suggestion.new') cbS.current?.(d as SuggestionEvent);
    });
    sub.subscribe();
    return () => {
      try {
        sub.unsubscribe();
        cfx.removeSubscription(sub);
      } catch { /* ignore */ }
    };
  }, [userId]);
}

/** Workspace stream: presence/status/profile updates for members. */
export function useWorkspaceStream(
  workspaceId: string | null,
  onUserUpdated: (u: PublicUser) => void,
): void {
  const cb = useRef(onUserUpdated);
  cb.current = onUserUpdated;
  useEffect(() => {
    if (!workspaceId || PREVIEW) return undefined;
    const cfx = client();
    const wch = `ws:${workspaceId}`;
    const sub = cfx.newSubscription(wch, subOpts(wch));
    sub.on('publication', (ctx: any) => {
      const d = ctx?.data;
      if (d?.type === 'user.updated' && d.user) cb.current(d.user as PublicUser);
    });
    sub.subscribe();
    return () => {
      try {
        sub.unsubscribe();
        cfx.removeSubscription(sub);
      } catch { /* ignore */ }
    };
  }, [workspaceId]);
}
